<template>
    <div class="sms-send-stage" title="">
        <section class="content">
            <div class="content-wrapper">
                <div
                    v-if="isSmsSendingBlockedByVerificationStatus"
                    class="verification-status"
                    data-qa="ktv-sms-verification-status"
                >
                    <KtvSmsVerificationStatusAlert
                        render-if-sending-blocked
                        show-button
                        :message="smsAccountVerification.errorDescription"
                        :rejection-date="smsAccountVerification.rejectionDate"
                        :verification-status="smsAccountVerification.status"
                    />
                </div>

                <DsLink
                    class="back-button"
                    data-qa="back-button"
                    @click="$emit('back')"
                >
                    <template #leading>
                        <DsIcon name="arrow-left" />
                    </template>

                    <template #default>
                        {{ $options.MESSAGE_CONSTANTS.back }}
                    </template>
                </DsLink>

                <h3>{{ $options.MESSAGE_CONSTANTS.title }}</h3>

                <div class="recipients">
                    <p class="subtitle">
                        {{ $options.MESSAGE_CONSTANTS.recipientSummarySubtitle }}
                    </p>


                    <div class="recipient-chips">
                        <DsChip
                            v-for="{ id, name, icon } in broadcast.recipientCriteria"
                            :key="id"
                            class="recipient-chip"
                        >
                            <span>{{ name }}</span>

                            <DsIcon slot="leading" :name="icon" />
                        </DsChip>
                    </div>

                    <SmsBroadcastMaxRecipientsAlert v-if="totalValidRecipientCount > $options.MAX_RECIPIENTS" class="max-recipients-alert" />
                </div>

                <div class="sms-broadcast-recipient-summary">
                    <span class="recipient-total">
                        This text message will be sent to
                        <strong> {{ sendingToCount }} </strong>
                        of the
                        <strong> {{ recipients.length }} </strong>
                        {{ phoneNumberTextRecipients }} you selected.
                    </span>

                    <div class="breakdown-details">
                        <div class="detail-header">
                            <span class="detail-title">
                                Sending to
                            </span>

                            <TooltipWithDelay class="breakdown-tooltip" position="bottom" :disabled="hideSendingTooltip">
                                <template #reference>
                                    <DsPopover
                                        :is-open="showSendingPopover"
                                        class="breakdown-popover"
                                        position="bottom"
                                        position-fixed
                                        @close="showSendingToPopover = false"
                                    >
                                        <template #reference>
                                            <DsLink
                                                dense
                                                class="tooltip-button"
                                                data-qa="sending-to-count-button"
                                                :class="{ active: showSendingToPopover }"
                                                @click="showSendingToPopover = !showSendingToPopover"
                                            >
                                                <span class="tooltip-text">{{ sendingToCount }}</span>
                                            </DsLink>
                                        </template>

                                        <template #default>
                                            <div class="breakdown-popover-container">
                                                <span>We will attempt to send to <strong data-qa="sending-to-count">{{ sendingToCount }}</strong> {{ phoneNumberTextSendingTo }}</span><span v-if="displayDoubleOptInCount">, <strong data-qa="double-opt-in-count">{{ doubleOptedInCount }}</strong> {{ phoneNumberTextSendingTo }} already completed double opt-in</span><span v-if="displaySingleOptInCount">, <strong data-qa="single-opt-in-count">{{ singleOptedInCount }}</strong> will receive the double opt-in request message</span><span>.</span>
                                            </div>
                                        </template>
                                    </DsPopover>
                                </template>

                                <template #default>
                                    <div>View details</div>
                                </template>
                            </TooltipWithDelay>
                        </div>

                        <div class="detail-header">
                            <span class="detail-title">
                                Not sending to
                            </span>

                            <TooltipWithDelay class="breakdown-tooltip" position="bottom" :disabled="hideNotSendingTooltip">
                                <template #reference>
                                    <DsPopover
                                        :is-open="showNotSendingPopover"
                                        class="breakdown-popover"
                                        position="bottom"
                                        position-fixed
                                        @close="showNotSendingToPopover = false"
                                    >
                                        <template #reference>
                                            <DsLink
                                                dense
                                                class="tooltip-button"
                                                data-qa="not-sending-to-count-button"
                                                :class="{ active: showNotSendingToPopover }"
                                                @click="showNotSendingToPopover = !showNotSendingToPopover"
                                            >
                                                <span class="tooltip-text">{{ notSendingToText }}</span>
                                            </DsLink>
                                        </template>

                                        <template #default>
                                            <div class="breakdown-popover-container">
                                                <span><strong data-qa="not-sending-to-count">{{ notSendingToCount }}</strong> {{ phoneNumberTextNotSendingTo }} {{ hasOrHaveTextNotSendingTo }} been removed from this text broadcast</span><span v-if="displayInvalidCount">, <strong data-qa="invalid-count">{{ invalidCount }}</strong> {{ isOrAreTextInvalidCount }} {{ phoneNumberTextInvalidCount }}</span><span v-if="displayPendingOptInCount">, <strong>{{ numbersReceivingOptin }}</strong> {{ isOrAreTextNumbersReceivingOptin }} pending opt-in</span><span v-if="displayOptedOutCount">, <strong data-qa="opted-out-count">{{ optedOutCount }}</strong> {{ hasOrHaveTextOptedOutCount }} opted out</span><span v-if="displayOverageCount">, and <span data-qa="overage-count">{{ overageText }}</span> {{ isOrAreTextOverageText }} over the broadcast sending limit</span><span>.</span>
                                            </div>
                                        </template>
                                    </DsPopover>
                                </template>

                                <template #default>
                                    <div>View details</div>
                                </template>
                            </TooltipWithDelay>
                        </div>

                        <div
                            data-qa="total-messages-sending-breakdown-details"
                            class="detail-header"
                        >
                            <span class="detail-title">
                                {{ $t('sms.broadcast.sendStage.totalMessagesSending') }}
                            </span>

                            <TooltipWithDelay class="breakdown-tooltip" position="bottom">
                                <template #reference>
                                    <DsPopover
                                        :is-open="showTotalMessagesPopover"
                                        class="breakdown-popover"
                                        position="bottom"
                                        position-fixed
                                        @close="showTotalMessagesPopover = false"
                                    >
                                        <template #reference>
                                            <DsLink
                                                dense
                                                class="tooltip-button"
                                                data-qa="total-messages-sending-button"
                                                :class="{ active: showTotalMessagesPopover }"
                                                @click="showTotalMessagesPopover = !showTotalMessagesPopover"
                                            >
                                                <span
                                                    class="tooltip-text"
                                                    data-qa="total-messages-sending-count"
                                                >{{
                                                    numMessagesTotal
                                                }}</span>
                                            </DsLink>
                                        </template>

                                        <template #default>
                                            <div class="breakdown-popover-container">
                                                <span data-qa="total-messages-sending-description">{{
                                                    $t('sms.broadcast.sendStage.totalMessagesDescription')
                                                }}</span>
                                            </div>
                                        </template>
                                    </DsPopover>
                                </template>

                                <template #default>
                                    <div>{{ $t('sms.broadcast.sendStage.viewDetails') }}</div>
                                </template>
                            </TooltipWithDelay>
                        </div>
                    </div>
                </div>

                <div class="pricing">
                    <div class="pricing-title">
                        <span class="subtitle"> Pricing </span>

                        <div class="pricing-info">
                            <span> {{ $options.MESSAGE_CONSTANTS.pricingMessageDoubleOptIn }} </span>
                        </div>
                    </div>

                    <div class="pricing-total-cost">
                        <span>{{ $t('sms.broadcast.sendStage.pricingTotalCostPrefix') }}</span>
                        <span data-qa="total-estimated-cost-usd">${{ totalCostUsd }}</span>
                    </div>

                    <div data-qa="pricing-statement">
                        <DsInlineAlert
                            type="info"
                            leading-icon
                        >
                            <template #default>
                                <span class="pricing-statement">
                                    After your included subscription messages,
                                    <strong>{{ $options.MESSAGE_CONSTANTS.youWillBeChargedPerText }}</strong>
                                    that's successfully sent and opted into. This resets with each new billing cycle.
                                </span>
                            </template>

                            <template #footer>
                                <DsFilledButton
                                    as="a"
                                    class="settings-link"
                                    :href="$options.MESSAGE_CONSTANTS.settingsLink"
                                    rel="noopener noreferer"
                                    target="_blank"
                                    dense
                                    gray
                                >
                                    <template #default>
                                        Track usage in Settings
                                    </template>
                                </DsFilledButton>
                            </template>
                        </DsInlineAlert>
                    </div>
                </div>

                <div class="message">
                    <span class="subtitle">
                        {{ $options.MESSAGE_CONSTANTS.message }}
                    </span>

                    <span class="message-content">{{ broadcast.messageContent }}</span>
                </div>

                <div class="sending-options">
                    <p class="subtitle">
                        {{ $options.MESSAGE_CONSTANTS.send }}
                    </p>

                    <DsRadio
                        v-model="timing"
                        class="timing-radio"
                        :val="sendNow"
                        data-qa="send-now-timing"
                        @input="update"
                    >
                        {{ $options.MESSAGE_CONSTANTS.now }}
                    </DsRadio>

                    <DsRadio
                        v-model="timing"
                        class="timing-radio"
                        :val="sendLater"
                        data-qa="send-later-timing"
                        @input="update"
                    >
                        {{ $options.MESSAGE_CONSTANTS.later }}
                    </DsRadio>

                    <div v-show="showTimingOptions" class="schedule-options">
                        <DsDatePicker
                            v-model="date"
                            :label="$options.MESSAGE_CONSTANTS.date"
                            class="schedule-options-field date"
                            disable-before-date="today"
                            @input="update"
                        />

                        <span class="at">{{ $options.MESSAGE_CONSTANTS.at }}</span>

                        <DsDatePicker
                            v-model="time"
                            :label="$options.MESSAGE_CONSTANTS.time"
                            class="schedule-options-field time"
                            time-only
                            @input="update"
                        />

                        <DsMultiselect
                            v-model="timeZone"
                            class="schedule-options-field time-zone"
                            searchable
                            :options="timeZoneOptions"
                            @input="update"
                        />
                    </div>
                </div>

                <div v-if="!loadingFtu && !writeSmsBroadcastTimingTutorialComplete" class="compliance-tip-container">
                    <div class="compliance-tip">
                        <span class="compliance-tip-message">{{ $options.MESSAGE_CONSTANTS.sendTip }}</span>
                        <DsIconButton name="x" class="compliance-tip-close" @click="markTimingTutorialComplete" />
                    </div>
                </div>

                <div class="actions">
                    <DsFilledButton
                        data-qa="send-button"
                        :disabled="isSmsSendingBlockedByVerificationStatus"
                        :loading="sending"
                        @click="checkSend"
                    >
                        {{ sendButtonText }}
                    </DsFilledButton>
                </div>

                <div class="sms-broadcast-footer-wrapper">
                    <SmsBroadcastFooter />
                </div>
            </div>
        </section>

        <portal to="root">
            <SmsBroadcastComplianceModal
                :is-open="isComplianceModalOpen"
                @close="isComplianceModalOpen = false"
                @send="markSendTutorialComplete"
            />

            <SmsBroadcastMaxRecipientsModal
                :is-open="isMaxRecipientsModalOpen"
                :total="totalValidRecipientCount"
                :not-receiving="totalValidRecipientCount - totalRecipientsReceivingBroadcast"
                @close="isMaxRecipientsModalOpen = false"
                @send="$emit('send')"
            />
        </portal>
    </div>
</template>

<script>
import moment from 'moment';
import TooltipWithDelay from '@/components/shared/TooltipWithDelay';

import SmsBroadcastFooter from '@/components/broadcast/sms/SmsBroadcastFooter';
import SmsBroadcastComplianceModal from '@/components/broadcast/sms/SmsBroadcastComplianceModal';
import SmsBroadcastMaxRecipientsModal from '@/components/broadcast/sms/SmsBroadcastMaxRecipientsModal';
import SmsBroadcastMaxRecipientsAlert from '@/components/broadcast/sms/SmsBroadcastMaxRecipientsAlert';
import KtvSmsVerificationStatusAlert from '@/components/automatedSms/KtvSmsVerificationStatusAlert';

import {
    getOptedOutRecipients,
    getValidRecipientCount,
    getSmsDeliveryTotalCostUsd,
} from '@/components/broadcast/utils/smsBroadcast.util';
import { getNextWeekday } from '@/components/broadcast/utils/builder.util';
import { getLocalizationInfo } from '@/api/broadcasts/smsBroadcasts';
import { loadFtu, updateFtuItem } from '@/ftu.utils';
import { FTU_TYPES } from '@/ftu.constants';
import {
    KMN_VERIFICATION_STATUS_TYPE,
    MAX_RECIPIENTS,
    SEND_NOW,
    SEND_LATER,
    SMS_OPT_STATUSES,
} from '@/components/broadcast/constants/smsBroadcast.constants';
import { getSmsSegments } from '@/components/broadcast/utils/sms-utils';
import { COST_PER_SMS_USD } from '@/components/automatedSms/sms.constants';

export const MESSAGE_CONSTANTS = {
    back: 'Edit message/recipients',
    title: 'Review and send',
    recipientSummarySubtitle: 'Recipient summary',
    contacts: 'contacts | contact | contacts',
    totalContacts: '{contactCount} {contacts}',
    phoneNumbers: 'phone numbers | phone number | phone numbers',
    totalPhoneNumbers: '{phoneNumberCount} {phoneNumbers}',
    totalSkippedPhoneNumbers: '{skippedPhoneNumberCount} won\'t be sent',
    recipientSummary: 'You added {totalContacts} to this broadcast, for a total of {totalPhoneNumbers}. ',
    skippedRecipients: 'Of those {totalPhoneNumbers}, {totalSkippedPhoneNumbers}.',
    why: 'Why?',
    skippedRecipientsTooltip: 'Of the {skippedRecipientsTooltipCount}, some have invalid phone numbers, and some phone numbers have opted out of your text message broadcasts.',
    skippedRecipientsTooltipCount: '{totalPhoneNumbers} messages that won’t be sent',
    sendingTo: 'Sending to',
    message: 'Message',
    now: 'Now',
    later: 'Later',
    schedule: 'Schedule broadcast',
    send: 'Send broadcast',
    date: 'Date',
    time: 'Time',
    at: 'at',
    contactTimeZone: 'Contact\'s time zone',
    optInWhy: 'Why?',
    optInWhyDescription: 'Your contacts must opt-in, indicating they want to receive broadcast messages from you. Any prior consent you may have obtained doesn\'t apply.',
    sendTip: 'Compliance Tip: The law restricts when you can send messages (generally only between 8 am and 9 pm in the contact\'s timezone).',
    settingsLink: '/app/nav/link?navSystem=nav.admin&navModule=nav.admin.my-account',
    pricingMessageDoubleOptIn: `You'll only pay $${COST_PER_SMS_USD} per message for texts that reach double opted-in recipients.`,
    youWillBeChargedPerText: ` you'll be charged $${COST_PER_SMS_USD} per text `,
};

export default {
    MAX_RECIPIENTS,
    MESSAGE_CONSTANTS,

    components: {
        SmsBroadcastFooter,
        SmsBroadcastComplianceModal,
        SmsBroadcastMaxRecipientsModal,
        SmsBroadcastMaxRecipientsAlert,
        TooltipWithDelay,
        KtvSmsVerificationStatusAlert,
    },

    props: {
        broadcast: {
            type: Object,
            required: true,
        },
        contactCount: {
            type: Number,
            default: 0,
        },
        optStatuses: {
            type: Array,
            default: () => [],
        },
        recipients: {
            type: Array,
            default: () => [],
        },
        sending: Boolean,
        appId: {
            type: String,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
        smsAccount: {
            type: Object,
            default: () => {},
        },
        smsAccountVerification: {
            type: Object,
            default: () => ({}),
        },
    },

    data() {
        return {
            date: null,
            time: null,
            timeZone: null,
            sendLater: SEND_LATER,
            sendNow: SEND_NOW,
            timing: SEND_NOW,
            pricePerMessage: COST_PER_SMS_USD,
            showNotSendingToPopover: false,
            showSendingToPopover: false,
            showSkippedRecipientsTooltip: false,
            showTotalMessagesPopover: false,
            isComplianceModalOpen: false,
            isMaxRecipientsModalOpen: false,
            appTimeZone: '',
            timeZoneOptions: [],
            loadingFtu: false,
            ftu: {},
            isSettingsModalOpen: false,
            meridiemPickrElement: null,
        };
    },

    mounted() {
        this.loadTimeZones();
        this.load();
        this.loadFtu();

        try {
            // add listener to address flatpickr shortcoming with a and p key events
            this.meridiemPickrElement = document.querySelector('.page_broadcastLandingPage > .flatpickr-calendar .flatpickr-am-pm');
            this.meridiemPickrElement.addEventListener('keyup', this.handleMeridiemKeyPress);
        } catch { /* do nothing if unable to add listener for a and p key presses to flatpickr meridiem selector */ }
    },

    beforeUnmount() {
        try {
            this.meridiemPickrElement.removeEventListener('keyup', this.handleMeridiemKeyPress);
        } catch { /* do nothing if unable to remove keyup listener from flatpickr meridiem selector */ }
    },


    computed: {
        broadcastLimitOverageCount() {
            const {
                invalidCount,
                numbersReceivingOptin,
                sendingToCount,
                recipientCount,
                optedOutCount,
            } = this;

            if (recipientCount > MAX_RECIPIENTS) {
                return recipientCount - sendingToCount - numbersReceivingOptin - invalidCount - optedOutCount;
            }

            return 0;
        },

        doubleOptedInCount() {
            const { optStatuses } = this;
            const { DOUBLE_OPT_IN } = SMS_OPT_STATUSES;

            return optStatuses.filter(({ statusKind }) => statusKind === DOUBLE_OPT_IN).length;
        },

        singleOptedInCount() {
            const { optStatuses, doubleOptedInCount } = this;
            const { SINGLE_OPT_IN } = SMS_OPT_STATUSES;

            const singleOptedInNumbers = optStatuses.filter(({ statusKind }) => statusKind === SINGLE_OPT_IN).length;

            if (doubleOptedInCount + singleOptedInNumbers >= MAX_RECIPIENTS) {
                return MAX_RECIPIENTS - doubleOptedInCount;
            }

            return Math.min(MAX_RECIPIENTS, singleOptedInNumbers);
        },

        sendingToCount() {
            return this.singleOptedInCount + this.doubleOptedInCount;
        },

        notSendingToCount() {
            const { recipients, sendingToCount } = this;

            return recipients.length - sendingToCount;
        },

        notSendingToText() {
            const { notSendingToCount } = this;

            if (notSendingToCount === 0) {
                return '0';
            }

            return `-${notSendingToCount}`;
        },

        hideSendingTooltip() {
            return this.showSendingToPopover || (this.sendingToCount === 0);
        },

        hideNotSendingTooltip() {
            return this.showNotSendingToPopover || (this.notSendingToCount === 0);
        },

        showSendingPopover() {
            return this.showSendingToPopover && (this.sendingToCount > 0);
        },

        showNotSendingPopover() {
            return this.showNotSendingToPopover && (this.notSendingToCount > 0);
        },

        displayDoubleOptInCount() {
            return this.doubleOptedInCount > 0;
        },

        displaySingleOptInCount() {
            return this.singleOptedInCount > 0;
        },

        displayInvalidCount() {
            return this.invalidCount > 0;
        },

        displayPendingOptInCount() {
            return this.numbersReceivingOptin > 0;
        },

        displayOptedOutCount() {
            return this.optedOutCount > 0;
        },

        displayOverageCount() {
            return this.broadcastLimitOverageCount > 0;
        },

        phoneNumberTextSendingTo() {
            return this.phoneNumberText(this.sendingToCount);
        },

        hasOrHaveTextSendingTo() {
            return this.hasOrHaveText(this.sendingToCount);
        },

        phoneNumberTextNotSendingTo() {
            return this.phoneNumberText(this.notSendingToCount);
        },

        hasOrHaveTextNotSendingTo() {
            return this.hasOrHaveText(this.notSendingToCount);
        },

        isOrAreTextInvalidCount() {
            return this.isOrAreText(this.invalidCount);
        },

        phoneNumberTextInvalidCount() {
            return this.invalidCount === 1 ? 'an invalid number' : 'invalid numbers';
        },

        isOrAreTextNumbersReceivingOptin() {
            return this.isOrAreText(this.numbersReceivingOptin);
        },

        hasOrHaveTextOptedOutCount() {
            return this.hasOrHaveText(this.optedOutCount);
        },

        isOrAreTextOverageText() {
            return this.isOrAreText(this.overageText);
        },

        overageText() {
            const {
                invalidCount,
                numbersReceivingOptin,
                sendingToCount,
                recipients,
                optedOutCount,
            } = this;

            if (recipients.length > MAX_RECIPIENTS) {
                return recipients.length - sendingToCount - numbersReceivingOptin - invalidCount - optedOutCount;
            }

            return '0';
        },

        phoneNumberTextRecipients() {
            return this.phoneNumberText(this.totalValidRecipientCount);
        },

        optedOutCount() {
            const { optStatuses } = this;

            return getOptedOutRecipients(optStatuses).length;
        },

        defaultTimeZone() {
            return this.timeZone;
        },

        showTimingOptions() {
            return this.timing === SEND_LATER;
        },

        numbersReceivingOptin() {
            const { sendingToCount, totalValidRecipientCount } = this;

            if (sendingToCount >= MAX_RECIPIENTS) {
                return 0;
            }

            return Math.min(totalValidRecipientCount, MAX_RECIPIENTS) - sendingToCount;
        },

        invalidCount() {
            const {
                optedOutCount,
                recipients,
                totalValidRecipientCount,
            } = this;

            return recipients.length - totalValidRecipientCount - optedOutCount;
        },

        sendButtonText() {
            return (this.timing === SEND_NOW ? MESSAGE_CONSTANTS.send : MESSAGE_CONSTANTS.schedule);
        },

        totalRecipientsReceivingBroadcast() {
            return this.totalValidRecipientCount >= MAX_RECIPIENTS ? MAX_RECIPIENTS : this.totalValidRecipientCount;
        },

        recipientCount() {
            return this.recipients.length;
        },

        totalValidRecipientCount() {
            const { optStatuses, recipients } = this;

            return getValidRecipientCount({ optStatuses, recipients });
        },

        writeSmsBroadcastTimingTutorialComplete() {
            return Boolean(this.ftu[FTU_TYPES.SMS_BROADCAST.TIMING]);
        },

        writeSmsBroadcastSendTutorialComplete() {
            return Boolean(this.ftu[FTU_TYPES.SMS_BROADCAST.SEND]);
        },

        numMessagesPerRecipient() {
            const { numMessages } = getSmsSegments(this.broadcast.messageContent);

            return numMessages ?? 0;
        },

        numMessagesTotal() {
            const { numMessagesPerRecipient, sendingToCount } = this;
            const total = numMessagesPerRecipient * sendingToCount;

            return total;
        },

        totalCostUsd() {
            const { numMessagesPerRecipient: numMessages, sendingToCount: numRecipients } = this;
            const amountUsd = getSmsDeliveryTotalCostUsd({ numMessages, numRecipients });

            return amountUsd;
        },

        isSmsSendingBlockedByVerificationStatus() {
            return this.smsAccountVerification.status
                && this.smsAccountVerification.status !== KMN_VERIFICATION_STATUS_TYPE.APPROVED;
        },
    },

    methods: {
        async load() {
            const {
                timing,
                timingDetails: {
                    date,
                    time,
                    timeZone,
                },
            } = this.broadcast.schedule;

            const now = moment();

            const nextWeekday = getNextWeekday(now);

            this.date = moment(date || nextWeekday).toDate();
            this.time = time || moment().hours('08').minutes('00').format('HHmm');
            this.timeZone = timeZone?.value ? timeZone : this.defaultTimeZone;
            this.timing = timing || 'sendNow';
            this.update();
        },

        async loadTimeZones() {
            try {
                const { appTimeZone, timeZoneOptions } = await getLocalizationInfo();

                this.appTimeZone = appTimeZone;

                this.timeZoneOptions = Object.keys(timeZoneOptions).map((key) => {
                    return { label: timeZoneOptions[key], value: key };
                });

                this.timeZone = this.timeZoneOptions.find((tz) => tz.value === this.appTimeZone);
            } catch (e) {
                this.$toast({ message: 'There was an error loading localization information' });
            }
        },

        async loadFtu() {
            this.loadingFtu = true;
            this.ftu = await loadFtu(this.appId, this.userId);
            this.loadingFtu = false;
        },

        update() {
            const {
                timing,
                time,
                date,
                timeZone,
            } = this;

            this.$emit('input', {
                timing,
                time,
                date,
                timeZone,
            });
        },

        markTimingTutorialComplete() {
            updateFtuItem(this.appId, this.userId, FTU_TYPES.SMS_BROADCAST.TIMING, true);
            this.ftu = {
                ...this.ftu,
                [FTU_TYPES.SMS_BROADCAST.TIMING]: true,
            };
        },

        markSendTutorialComplete() {
            this.isComplianceModalOpen = false;
            updateFtuItem(this.appId, this.userId, FTU_TYPES.SMS_BROADCAST.SEND, true);
            this.ftu = {
                ...this.ftu,
                [FTU_TYPES.SMS_BROADCAST.SEND]: true,
            };
            this.checkSend();
        },

        checkSend() {
            if (!this.writeSmsBroadcastSendTutorialComplete) {
                this.isComplianceModalOpen = true;
            } else if (this.totalValidRecipientCount >= MAX_RECIPIENTS) {
                this.isMaxRecipientsModalOpen = true;
            } else {
                this.$emit('send');
            }
        },

        phoneNumberText(count) {
            return count === 1 ? 'phone number' : 'phone numbers';
        },

        hasOrHaveText(count) {
            return count === 1 ? 'has' : 'have';
        },

        isOrAreText(count) {
            return count === 1 ? 'is' : 'are';
        },

        handleMeridiemKeyPress(event) {
            const noon = 1200;
            const twentyFourHours = 2400;
            const eventKey = String(event.key).toLocaleLowerCase();

            try {
                // if meridiem change, add 12 hours, modulo 24, pad left with 4 zero chars
                if ((eventKey === 'a' && +this.time >= noon) || (eventKey === 'p' && +this.time < noon)) {
                    this.time = String((+this.time + noon) % twentyFourHours).padStart(4, '0');
                    this.update();
                }
            } catch { /* do nothing */ }
        },
    },
};
</script>
<style lang="scss">
    .sms-send-stage {
        .multiselect input.search-input{
            padding-top: 0px;
        }
    }
</style>
<style lang="scss" scoped>
    .back-button {
        align-self: flex-start;
        margin-bottom: $spacing-100;
    }

    .actions {
        padding: $spacing-200 0;
    }

    .content {
        overflow-y: auto;
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 3;
        overflow-x: hidden;

        &-wrapper {
            padding-top: $spacing-400;
            height: 100%;
            width: 70%;
            display: flex;
            flex-direction: column;

            @media($small) {
                width: 100%;
                padding: $spacing-200 $spacing-300 0;
            }
        }
    }

    h3 {
        font-family: $font-family-secondary-title;
        font-weight: 600;
        padding-bottom: $spacing-200;
    }

    .subtitle {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        font-weight: $font-weight-semibold;
        margin: $spacing-100 0;
    }

    .recipients {
        padding-bottom: $spacing-200;
    }

    .message {
        display: flex;
        flex-direction: column;
        word-wrap: break-word;
    }

    .message-content {
        font-size: $font-size-md;
    }

    .timing-radio {
        padding: $spacing-100 0;
    }

    .schedule-options {
        display: flex;
        padding: $spacing-200 0;

        &-field {
            margin: 0 $spacing-200;

            @media($small) {
                margin: 0;
            }
        }

        .date {
            min-width: px-to-rem(200px);
            flex: 3;
        }

        .at {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        .time {
            flex: 2;
        }

        .time-zone {
            flex: 4;
        }

        @media(max-width: px-to-rem(1500px)) {
            flex-direction: column;
            padding-bottom: 0;

            &-field, .at {
                margin-bottom: $spacing-200;
            }

            .date, .time, .time-zone {
                width: 100%;
            }
        }
    }

    .recipient-chip {
        margin-right: $spacing-100;
        margin-bottom: $spacing-100;
    }

    .max-recipients-alert {
        margin: $spacing-200 0;
    }

    .popover-content {
        padding: $spacing-200;
    }

    .sms-broadcast-footer-wrapper {
        margin-top: auto;
    }

    .compliance-tip-container {
        border-radius: $border-radius * 0.5;
        margin: $spacing-300 0;
        display: flex;
    }

    .compliance-tip {
        background-color: $color-gray-100;
        display: flex;
    }

    .compliance-tip-message {
        padding: $spacing-200;
        font-size: $font-size-md;
        line-height: $line-height-lg;
    }

    .compliance-tip-close {
        max-height: px-to-rem(40px);
        margin: $spacing-050;
    }

    .message, .sending-options, .pricing-title {
        margin-top: $spacing-400;
    }

    div.sms-send-stage {
        display: flex;
        height: calc(100vh - #{px-to-rem(56px)});
    }

    .breakdown-details {
        display: flex;
        margin-top: $spacing-200;
    }

    .detail-header:not(:last-child) {
        @include margin-end(4rem);
    }

    .detail-header {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: $color-text-subtle;
    }

    .recipient-total {
        font-size: $font-size-md;
        line-height: $line-height-lg;
        margin-bottom: $spacing-100;
    }

    .detail-title {
        font-size: $font-size-sm;
        color: $color-text-subtle;
    }

    .pricing {
        display: flex;
        flex-direction: column;
        --icon-color: #{$color-text-subtle};
        --icon-size: 1rem;
    }

    .pricing-info {
        margin-bottom: $spacing-100
    }

    .settings-link .icon {
        --icon-color: #{$color-blue};
    }

    .tooltip-button {
        --icon-size: 1rem;
        color: $color-blue;
        text-decoration: none;

        &.inline {
            margin: -$spacing-100 0;
        }
    }

    .tooltip-text {
        font-size: $font-size-jumbo;
    }

    .tooltip-container.breakdown-tooltip {
        padding: 0;
    }

    .breakdown-popover {
        --popover-width: #{px-to-rem(360)};
    }

    .tooltip-container,
    .breakdown-popover-container {
        padding: $spacing-200;
        font-size: $font-size-md;
        line-height: $line-height-lg;
        color: $color-black;
    }

    .pricing-total-cost {
        display: flex;
        flex-direction: column;
        align-items: flex-start;

        span:nth-child(1) {
            color: $color-text-subtle;
        }

        span:nth-child(2) {
            color: $color-black;
            font-size: $font-size-jumbo;
        }
    }

    .verification-status:not(:empty) {
        margin-bottom: $spacing-300;
        width: 100%;
    }
</style>
